import { RouterProvider } from "@tanstack/react-router";

import { createRouter } from "@tanstack/react-router";

import "./index.css";

// Import the generated route tree
import { QueryClientProvider, useSuspenseQuery } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen.ts";

import { getUserSessionQueryOptions } from "@/lib/data/react-query/auth/auth.ts";
import { isAuthenticated } from "@/lib/utils/auth";
import {
  VITE_AWS_USER_POOL_CLIENT_ID,
  VITE_AWS_USER_POOL_ID,
  VITE_GOOGLE_MAPS_API_KEY,
} from "@/lib/data/static/importEnv.ts";
import { ModalProvider } from "@/providers/ModalProvider.tsx";
import { type Libraries, useJsApiLoader } from "@react-google-maps/api";
import { TourProvider } from "@reactour/tour";
import { ErrorBoundary } from "@sentry/react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Amplify } from "aws-amplify";
import { queryClient } from "@/lib/data/react-query/client";
import { Suspense, useEffect } from "react";
import { ThemeProvider } from "./components/ThemeProvider.tsx";
import { GlobalNotFound } from "./components/ui/GlobalNotFound/GlobalNotFound.tsx";
import LawWorksLoader from "./components/ui/LawWorksLoader/LawWorksLoader.tsx";
import LogoutButton from "./components/ui/LogoutButton/LogoutButton.tsx";
import { ServerErrorFallbackCard } from "./components/ui/ServerErrorFallback/ServerErrorFallbackCard.tsx";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: VITE_AWS_USER_POOL_ID, // Your Cognito User Pool ID
      userPoolClientId: VITE_AWS_USER_POOL_CLIENT_ID, // Your App Client ID
    },
  },
});

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPendingMinMs: 0,
  context: {
    queryClient,
    session: {},
  },
  defaultNotFoundComponent: () => {
    return <GlobalNotFound />;
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
const libraries: Libraries = ["places"];

function InnerApp() {
  const { data: session, isFetching: isSessionFetching } = useSuspenseQuery(
    getUserSessionQueryOptions(),
  );
  useJsApiLoader({
    googleMapsApiKey: VITE_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  useEffect(() => {
    if (isSessionFetching) {
      return;
    }

    if (isAuthenticated({ session })) {
      return;
    }

    const isPublicAuthRoute =
      window.location.pathname.startsWith("/login") ||
      window.location.pathname.startsWith("/forgot-password") ||
      window.location.pathname.startsWith("/reset-password") ||
      window.location.pathname.startsWith("/change-password") ||
      window.location.pathname.startsWith("/register") ||
      window.location.pathname.startsWith("/sign") ||
      window.location.pathname.startsWith("/file-share");

    if (isPublicAuthRoute) {
      return;
    }

    void router.navigate({
      to: "/login",
      search: { redirect: window.location.href },
    });
  }, [session, isSessionFetching]);

  return (
    <ErrorBoundary
      fallback={
        <div className="p-4">
          <ServerErrorFallbackCard />
        </div>
      }
    >
      <RouterProvider router={router} context={{ session }} />
    </ErrorBoundary>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <DndProvider backend={HTML5Backend}>
        <ModalProvider>
          <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            <ErrorBoundary
              fallback={
                <div className="p-4">
                  <ServerErrorFallbackCard />
                  <LogoutButton />
                </div>
              }
            >
              <TourProvider
                steps={[]}
                styles={{
                  popover: (base) => ({
                    ...base,
                    borderRadius: 8,
                    border: "none",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  }),
                  maskArea: (base) => ({
                    ...base,
                    rx: 8,
                    ry: 8,
                  }),
                  badge: (base) => ({
                    ...base,
                    background: "hsl(var(--primary))",
                    color: "hsl(var(--primary-foreground))",
                  }),
                  dot: (base) => ({
                    ...base,
                    background: "hsl(var(--primary))",
                  }),
                  highlightedArea: (base) => ({ ...base, stroke: "none" }), // avoid outline
                }}
              >
                <Suspense fallback={<LawWorksLoader />}>
                  <InnerApp />
                </Suspense>
              </TourProvider>
            </ErrorBoundary>
          </ThemeProvider>
        </ModalProvider>
      </DndProvider>
    </QueryClientProvider>
  );
}
